import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { buildMultitranUrl } from '../utils/dictionaryLinks';
import { getCitationUrl } from '../utils/citationLinks';
import { ANNOTATION_TYPE_BY_KEY } from './annotationTypes';
import './AnnotationTooltip.css';


// Only show the dictionary lookup affordance for term-level annotations.
// PRESERVATION (dates/IDs) and CRITIC_FIX/CONSISTENCY don't benefit from a
// translator going to Multitran for them.
const DICTIONARY_LOOKUP_TYPES = new Set(['glossary', 'proper_noun']);

function AnnotationTooltip({ annotation, position, sourceLang, targetLang, onMouseEnter, onMouseLeave }) {
  const { t } = useTranslation('chat');

  if (!annotation || !position) return null;

  const config = ANNOTATION_TYPE_BY_KEY[annotation.type];

  const tooltipStyle = {
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    zIndex: 'var(--z-tooltip)',
  };

  const lookupTerm = annotation.source_text || annotation.text;
  const showDictionaryLink =
    DICTIONARY_LOOKUP_TYPES.has(annotation.type) && lookupTerm && sourceLang && targetLang;
  const multitranUrl = showDictionaryLink
    ? buildMultitranUrl(lookupTerm, sourceLang, targetLang)
    : null;

  return ReactDOM.createPortal(
    <div
      className="annotation-tooltip"
      style={tooltipStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="annotation-tooltip-header">
        <span className={`annotation-type-badge ${config.className}`}>
          {t(`legend.${config.i18nKey}.label`)}
        </span>
        {annotation.severity && (
          <span className={`annotation-severity severity-${annotation.severity}`}>
            {annotation.severity}
          </span>
        )}
      </div>

      {!annotation.source_text && !annotation.explanation && !annotation.note && (
        <div className="annotation-tooltip-row">
          <span className="annotation-tooltip-value">{t(`legend.${config.i18nKey}.desc`)}</span>
        </div>
      )}

      {annotation.source_text && (
        <div className="annotation-tooltip-row">
          <span className="annotation-tooltip-label">{t('annotations.source', 'Source')}:</span>
          <span className="annotation-tooltip-value">{annotation.source_text}</span>
        </div>
      )}

      {annotation.explanation && (
        <div className="annotation-tooltip-row">
          <span className="annotation-tooltip-label">{t('annotations.reason', 'Reason')}:</span>
          <span className="annotation-tooltip-value">{annotation.explanation}</span>
        </div>
      )}

      {annotation.note && (
        <div className="annotation-tooltip-row">
          <span className="annotation-tooltip-label">{t('annotations.note', 'Note')}:</span>
          <span className="annotation-tooltip-value">{annotation.note}</span>
        </div>
      )}

      {annotation.alternatives && annotation.alternatives.length > 0 && (
        <div className="annotation-tooltip-row">
          <span className="annotation-tooltip-label">{t('annotations.alternatives', 'Alternatives')}:</span>
          <span className="annotation-tooltip-value">
            {annotation.alternatives.join(', ')}
          </span>
        </div>
      )}

      {annotation.citation && (
        <div className="annotation-tooltip-row">
          <span className="annotation-tooltip-label">{t('annotations.standard', 'Standard')}:</span>
          <span className="annotation-tooltip-value">
            {getCitationUrl(annotation.citation) ? (
              <a
                href={getCitationUrl(annotation.citation)}
                target="_blank"
                rel="noopener noreferrer"
                className="annotation-tooltip-link"
                onClick={(e) => e.stopPropagation()}
              >
                {annotation.citation}
                <ExternalLink size={11} />
              </a>
            ) : (
              annotation.citation
            )}
          </span>
        </div>
      )}

      {multitranUrl && (
        <div className="annotation-tooltip-footer">
          <a
            href={multitranUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="annotation-tooltip-link"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={12} />
            <span>{t('annotations.openInMultitran', 'Open in Multitran')}</span>
          </a>
        </div>
      )}
    </div>,
    document.body
  );
}

export default AnnotationTooltip;
